import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Download, Trash2, Upload, Mic, Music, RefreshCw, Send, Pencil } from "lucide-react";
import client, { API } from "@/lib/glossyApi";
import { Header } from "./shared";

const absUrl = (path) => (path?.startsWith("http") || path?.startsWith("/") ? path : `${API}/files/${path}`);

const FORMATS = [
  { id: "reel", label: "Short reel", note: "~20 sec" },
  { id: "explainer", label: "Explainer", note: "~1 min" },
  { id: "feature", label: "Feature", note: "~2 min" },
];
const VOICES = [
  { id: "fable", label: "Fable — British storyteller" },
  { id: "coral", label: "Coral — warm female" },
  { id: "shimmer", label: "Shimmer — bright female" },
  { id: "onyx", label: "Onyx — deep & bold" },
  { id: "nova", label: "Nova — warm & bright" },
];
const VoicePicker = ({ value, onChange, tid }) => (
  <div className="flex gap-1.5 flex-wrap mt-2">
    {VOICES.map((v) => (
      <button key={v.id} onClick={() => onChange(v.id)}
        className={`overline text-[9px] px-2 py-1 border transition-colors ${value === v.id ? "bg-ink text-cream border-ink" : "border-ink/30 text-ink/60 hover:border-ink"}`}
        data-testid={`${tid}-${v.id}`}>{v.label}</button>
    ))}
  </div>
);
const MOODS = [
  { id: "upbeat", label: "Upbeat" },
  { id: "drive", label: "Dark drive" },
  { id: "funk", label: "Funky" },
  { id: "classy", label: "Classy & calm" },
  { id: "dream", label: "Dreamy" },
  { id: "none", label: "No music" },
];

function Composer({ onCut }) {
  const [prompt, setPrompt] = useState("");
  const [format, setFormat] = useState("reel");
  const [source, setSource] = useState("magazine");
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [uploads, setUploads] = useState([]);
  const [voiceover, setVoiceover] = useState(true);
  const [voice, setVoice] = useState("fable");
  const [music, setMusic] = useState("upbeat");
  const [energy, setEnergy] = useState("fast");
  const [look, setLook] = useState("ink");
  const [mode, setMode] = useState("magazine");
  const [style, setStyle] = useState("");
  const [houseStyle, setHouseStyle] = useState("");

  useEffect(() => {
    client.get("/admin/settings").then((r) => {
      const hs = r.data.video_house_style || "edgy Gorillaz-style 2D cartoon, bold ink outlines, flat cel shading, gritty urban texture, teal-magenta-gold palette";
      setHouseStyle(hs);
      setStyle((s) => s || hs);
    }).catch(() => {});
  }, []);

  const saveHouseStyle = async () => {
    try {
      await client.patch("/admin/settings", { video_house_style: style.trim() });
      setHouseStyle(style.trim());
      toast.success("House style saved — every new film starts from this look.");
    } catch { toast.error("Could not save the style."); }
  };
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    client.get("/admin/members").then((r) => setMembers(r.data || [])).catch(() => {});
  }, []);

  const uploadFiles = async (files) => {
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await client.post("/admin/productions/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setUploads((u) => [...u, r.data.key]);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "That photo would not upload.");
      }
    }
  };

  const cut = async () => {
    if (!prompt.trim()) return toast.error("Tell the desk what the video is about first.");
    setBusy(true);
    try {
      await client.post("/admin/productions", {
        prompt, format, source, member_id: memberId || null,
        upload_keys: uploads, voiceover, voice, music, energy, look, mode, style,
      });
      toast.success("Storyboard planned — review it in the library below, then cut.");
      setPrompt("");
      setUploads([]);
      onCut();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The desk could not start the cut.");
    } finally {
      setBusy(false);
    }
  };

  const pill = (active) =>
    `overline text-[10px] px-3 py-2 border transition-colors ${active ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"}`;

  return (
    <div className="border border-ink/20 bg-white p-5 sm:p-6 mb-8" data-testid="video-desk-composer">
      <p className="overline text-ink mb-1">Cut a new video</p>
      <p className="font-sans text-xs text-ink/60 mb-4">One brief, one button — the desk writes it, narrates it, scores it and cuts it. Roughly 5–10p a video; magazine and member photos are free.</p>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setMode("magazine")} className={pill(mode === "magazine")} data-testid="video-desk-mode-magazine">Magazine film</button>
        <button onClick={() => setMode("create")} className={pill(mode === "create")} data-testid="video-desk-mode-create">Create anything</button>
      </div>
      {mode === "create" && (
        <div className="flex gap-2 mb-1">
          <input value={style} onChange={(e) => setStyle(e.target.value)}
            placeholder='Visual style — e.g. "edgy Gorillaz-style cartoon", "dreamy watercolour", "cinematic photoreal"'
            className="flex-1 bg-cream border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold"
            data-testid="video-desk-style" />
          <button onClick={saveHouseStyle} disabled={!style.trim() || style.trim() === houseStyle}
            className="overline text-[9px] px-3 py-2 border border-gloss-gold hover:bg-gloss-gold transition-colors disabled:opacity-40 whitespace-nowrap"
            data-testid="video-desk-save-style">Save as house style</button>
        </div>
      )}
      {mode === "create" && (
        <p className="font-mono text-[9px] text-ink/40 mb-4">{style.trim() === houseStyle ? "Using your house style — every film keeps the same look." : "Custom style for this film only — press Save to make it the house look."}</p>
      )}
      <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
        placeholder='e.g. "A welcome video for readers — what S/ME is and why to keep coming back" or "A 20-second reel about Kayes of Cardiff"'
        className="w-full bg-cream border border-ink/20 px-3 py-2.5 font-sans text-sm focus:outline-none focus:border-gloss-gold mb-4"
        data-testid="video-desk-prompt" />

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <p className="overline text-[10px] text-ink/60 mb-2">Length</p>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => (
              <button key={f.id} onClick={() => setFormat(f.id)} className={pill(format === f.id)} data-testid={`video-desk-format-${f.id}`}>
                {f.label} · {f.note}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="overline text-[10px] text-ink/60 mb-2">Pictures from</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setSource("magazine")} className={pill(source === "magazine")} data-testid="video-desk-source-magazine">The magazine</button>
            <button onClick={() => setSource("member")} className={pill(source === "member")} data-testid="video-desk-source-member">A member</button>
            <button onClick={() => { setSource("uploads"); fileRef.current?.click(); }} className={pill(source === "uploads")} data-testid="video-desk-source-uploads">
              <Upload size={11} className="inline mr-1 -mt-0.5" />My photos
            </button>
          </div>
          {source === "member" && (
            <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
              className="mt-2 w-full bg-cream border border-ink/20 px-3 py-2 font-sans text-sm focus:outline-none" data-testid="video-desk-member-select">
              <option value="">Pick a member…</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.business_name}</option>)}
            </select>
          )}
          {source === "uploads" && (
            <div className="mt-2 flex flex-wrap gap-2 items-center">
              {uploads.map((k) => (
                <img key={k} src={absUrl(k)} alt="" className="w-12 h-12 object-cover border border-ink/20" />
              ))}
              <button onClick={() => fileRef.current?.click()} className="w-12 h-12 border border-dashed border-ink/40 text-ink/50 hover:border-ink flex items-center justify-center" data-testid="video-desk-add-photo">+</button>
            </div>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={(e) => { uploadFiles([...e.target.files]); e.target.value = ""; }} data-testid="video-desk-file-input" />
        </div>
        <div>
          <p className="overline text-[10px] text-ink/60 mb-2"><Mic size={11} className="inline mr-1 -mt-0.5" />Voiceover</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setVoiceover(!voiceover)} className={pill(voiceover)} data-testid="video-desk-vo-toggle">{voiceover ? "Narrated" : "No voice"}</button>
            {voiceover && VOICES.map((v) => (
              <button key={v.id} onClick={() => setVoice(v.id)} className={pill(voice === v.id)} data-testid={`video-desk-voice-${v.id}`}>{v.label}</button>
            ))}
          </div>
        </div>
        <div>
          <p className="overline text-[10px] text-ink/60 mb-2">Energy & look</p>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setEnergy("fast")} className={pill(energy === "fast")} data-testid="video-desk-energy-fast">Fast & punchy</button>
            <button onClick={() => setEnergy("classy")} className={pill(energy === "classy")} data-testid="video-desk-energy-classy">Classy & slow</button>
            <button onClick={() => setLook("ink")} className={pill(look === "ink")} data-testid="video-desk-look-ink">Ink & gold</button>
            <button onClick={() => setLook("cream")} className={pill(look === "cream")} data-testid="video-desk-look-cream">Cream</button>
            <button onClick={() => setLook("colour")} className={pill(look === "colour")} data-testid="video-desk-look-colour">Loud colour</button>
          </div>
        </div>
        <div>
          <p className="overline text-[10px] text-ink/60 mb-2"><Music size={11} className="inline mr-1 -mt-0.5" />Music</p>
          <div className="flex flex-wrap gap-2">
            {MOODS.map((m) => (
              <button key={m.id} onClick={() => setMusic(m.id)} className={pill(music === m.id)} data-testid={`video-desk-music-${m.id}`}>{m.label}</button>
            ))}
          </div>
        </div>
      </div>

      <button onClick={cut} disabled={busy}
        className="mt-5 overline text-[11px] px-6 py-3 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50 flex items-center gap-2"
        data-testid="video-desk-cut-btn">
        <Clapperboard size={13} />{busy ? "Planning…" : "Plan the film"}
      </button>
    </div>
  );
}

const BGS = ["ink", "cream", "gold", "flame"];
const BG_SWATCH = { ink: "#100f0d", cream: "#f7f3eb", gold: "#c6a15b", flame: "#c43c1c" };

function CreateBoard({ p, onChanged }) {
  const [busy, setBusy] = useState("");
  const [scenes, setScenes] = useState(p.plan?.scenes || []);
  const [addNote, setAddNote] = useState("");
  const [mood, setMood] = useState(p.music || "upbeat");
  const [vo, setVo] = useState(p.vo_script || "");
  const [voice, setVoice] = useState(p.voice || "fable");
  useEffect(() => { setScenes(p.plan?.scenes || []); }, [p.plan]);
  useEffect(() => { setVo(p.vo_script || ""); }, [p.vo_script]);
  useEffect(() => { setVoice(p.voice || "fable"); }, [p.voice]);
  const drawn = scenes.filter((s) => s.frame_key).length;
  const editable = ["planned", "ready", "failed"].includes(p.status);
  const cutLabel = p.status === "planned" ? "Cut it" : "Re-cut";
  const upd = (i, patch) => setScenes((s) => s.map((sc, j) => (j === i ? { ...sc, ...patch } : sc)));
  const act = async (fn, label) => {
    setBusy(label);
    try { await fn(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work — try again."); }
    finally { setBusy(""); }
  };
  const savePlan = () => client.put(`/admin/productions/${p.id}/plan`, { scenes, music: mood, vo_script: vo, voice });
  return (
    <div className="p-4 bg-cream/60 border-t border-ink/10" data-testid={`createboard-${p.id}`}>
      <p className="overline text-[10px] text-ink mb-1">{p.style ? `Style: ${p.style}` : "Create anything"} — {drawn}/{scenes.length} frames drawn</p>
      <div className="grid grid-cols-3 gap-2 max-h-80 overflow-y-auto pr-1">
        {scenes.map((sc, i) => (
          <div key={i} className="border border-ink/15 bg-white" data-testid={`frame-${p.id}-${i}`}>
            {sc.frame_key ? (
              <img src={absUrl(sc.frame_key)} alt="" className="w-full aspect-[9/16] object-cover" />
            ) : (
              <div className="w-full aspect-[9/16] bg-ink/10 flex items-center justify-center p-2">
                <p className="font-mono text-[8px] text-ink/50 text-center line-clamp-5">{sc.image_prompt}</p>
              </div>
            )}
            <div className="p-1.5 space-y-1">
              <input value={sc.text || ""} onChange={(e) => upd(i, { text: e.target.value })} placeholder="on-screen text"
                className="w-full bg-cream border-b border-ink/20 font-mono text-[9px] px-1 py-0.5 focus:outline-none" data-testid={`frame-text-${p.id}-${i}`} />
              <div className="flex items-center gap-1">
                <input type="number" step="0.5" min="2" max="4.5" value={sc.secs}
                  onChange={(e) => upd(i, { secs: parseFloat(e.target.value) || 3 })}
                  className="w-12 bg-cream border-b border-ink/20 font-mono text-[9px] px-1 focus:outline-none" data-testid={`frame-secs-${p.id}-${i}`} />
                {editable && sc.frame_key && (
                  <button onClick={() => act(async () => { await savePlan(); await client.post(`/admin/productions/${p.id}/boards`, { scene_index: i }); }, `r${i}`)}
                    className="overline text-[8px] text-gloss-gold hover:underline" data-testid={`frame-redraw-${p.id}-${i}`}>
                    {busy === `r${i}` ? "…" : "Redraw ~6p"}
                  </button>
                )}
                <button onClick={() => setScenes((s) => s.filter((_, j) => j !== i))} className="ml-auto text-red-400 hover:text-red-600 text-xs" data-testid={`frame-del-${p.id}-${i}`}>✕</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {p.status === "drawing" && <p className="overline text-[10px] text-ink/60 mt-3 animate-pulse" data-testid={`drawing-${p.id}`}>Drawing frames — a minute or two…</p>}
      {editable && (
        <>
          <div className="flex gap-2 mt-3">
            <input value={addNote} onChange={(e) => setAddNote(e.target.value)}
              placeholder='Add a scene with AI — e.g. "she flips the sign to SOLD OUT, delighted"'
              className="flex-1 bg-white border border-ink/20 px-2 py-1.5 font-sans text-xs focus:outline-none focus:border-gloss-gold" data-testid={`add-scene-input-${p.id}`} />
            <button onClick={() => act(async () => { await savePlan(); await client.post(`/admin/productions/${p.id}/add-scene`, { instruction: addNote }); setAddNote(""); }, "add")}
              disabled={!!busy || !addNote.trim()}
              className="overline text-[9px] px-3 py-2 border border-gloss-gold hover:bg-gloss-gold transition-colors disabled:opacity-40 whitespace-nowrap" data-testid={`add-scene-btn-${p.id}`}>
              {busy === "add" ? "Writing…" : "Add scene ~1p"}
            </button>
          </div>
          <div className="flex gap-1.5 mt-3 flex-wrap items-center">
            <span className="overline text-[9px] text-ink/50">Music:</span>
            {MOODS.map((m) => (
              <button key={m.id} onClick={() => setMood(m.id)}
                className={`overline text-[9px] px-2 py-1 border ${mood === m.id ? "bg-ink text-cream border-ink" : "border-ink/25 hover:border-ink"}`}
                data-testid={`board-music-${m.id}-${p.id}`}>{m.label}</button>
            ))}
          </div>
          {p.voiceover && (
            <>
              <p className="overline text-[10px] text-ink/60 mt-3 mb-1">What the narrator says {vo !== (p.vo_script || "") || voice !== (p.voice || "fable") ? "— changed (new voice take ~3p)" : "— unchanged (free on re-cut)"}</p>
              <textarea value={vo} onChange={(e) => setVo(e.target.value)} rows={3}
                className="w-full bg-white border border-ink/20 px-2 py-1.5 font-sans text-xs focus:outline-none focus:border-gloss-gold" data-testid={`createboard-vo-${p.id}`} />
              <VoicePicker value={voice} onChange={setVoice} tid={`createboard-voice-${p.id}`} />
            </>
          )}
          <div className="flex gap-2 mt-3 flex-wrap">
            {drawn < scenes.length && (
              <button onClick={() => act(async () => { await savePlan(); await client.post(`/admin/productions/${p.id}/boards`, {}); }, "draw")} disabled={!!busy}
                className="overline text-[10px] px-4 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50"
                data-testid={`draw-frames-${p.id}`}>
                {busy === "draw" ? "Sending…" : `Draw the frames (~${Math.max(1, scenes.length - drawn) * 6}p)`}
              </button>
            )}
            {drawn === scenes.length && scenes.length > 0 && (
              <>
                <button onClick={() => act(async () => { await savePlan(); await client.post(`/admin/productions/${p.id}/render`, { motion: "animate" }); }, "anim")} disabled={!!busy}
                  className="overline text-[10px] px-4 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50"
                  data-testid={`render-animate-${p.id}`}>
                  {busy === "anim" ? "Sending…" : `${cutLabel} — animated (~${scenes.length * 20}p)`}
                </button>
                <button onClick={() => act(async () => { await savePlan(); await client.post(`/admin/productions/${p.id}/render`, { motion: "stills" }); }, "still")} disabled={!!busy}
                  className="overline text-[10px] px-4 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50"
                  data-testid={`render-stills-${p.id}`}>
                  {busy === "still" ? "Sending…" : `${cutLabel} — cinematic pans (free)`}
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Storyboard({ p, onChanged }) {
  const [scenes, setScenes] = useState(p.plan?.scenes || []);
  const [vo, setVo] = useState(p.vo_script || "");
  const [voice, setVoice] = useState(p.voice || "fable");
  const [busy, setBusy] = useState("");

  const upd = (i, patch) => setScenes((s) => s.map((sc, j) => (j === i ? { ...sc, ...patch } : sc)));
  const move = (i, d) => setScenes((s) => {
    const c = [...s]; const j = i + d;
    if (j < 0 || j >= c.length) return s;
    [c[i], c[j]] = [c[j], c[i]]; return c;
  });
  const del = (i) => setScenes((s) => s.filter((_, j) => j !== i));
  const addCard = () => setScenes((s) => [...s, { type: "card", lines: ["New line"], sub: null, bg: "ink", big: false, secs: 1.2 }]);

  const save = async (thenRender) => {
    setBusy(thenRender ? "render" : "save");
    try {
      await client.put(`/admin/productions/${p.id}/plan`, { scenes, vo_script: vo, voice });
      if (thenRender) {
        await client.post(`/admin/productions/${p.id}/render`);
        toast.success("Cutting the film — a minute or three.");
      } else {
        toast.success("Storyboard saved.");
      }
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save that.");
    } finally {
      setBusy("");
    }
  };

  const total = scenes.reduce((a, s) => a + (parseFloat(s.secs) || 1), 0);
  return (
    <div className="p-4 bg-cream/60 border-t border-ink/10" data-testid={`storyboard-${p.id}`}>
      <p className="overline text-[10px] text-ink mb-1">Storyboard — edit anything, then cut. ~{total.toFixed(1)}s</p>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
        {scenes.map((sc, i) => (
          <div key={i} className="flex items-center gap-2 bg-white border border-ink/15 p-2" data-testid={`scene-${p.id}-${i}`}>
            {sc.type === "visual" ? (
              <span className="font-mono text-[10px] text-ink/60 flex-1 truncate">📄 {sc.what}</span>
            ) : (
              <>
                <button onClick={() => { const k = BGS[(BGS.indexOf(sc.bg || "ink") + 1) % BGS.length]; upd(i, { bg: k }); }}
                  title={`Background: ${sc.bg || "ink"} — click to change`}
                  className="w-5 h-5 border border-ink/30 shrink-0" style={{ background: BG_SWATCH[sc.bg || "ink"] }}
                  data-testid={`scene-bg-${p.id}-${i}`} />
                <input value={(sc.lines || []).join(" / ")} onChange={(e) => upd(i, { lines: e.target.value.split("/").map((x) => x.trim()).filter(Boolean) })}
                  className="flex-1 bg-transparent border-b border-ink/20 font-sans text-xs px-1 py-0.5 focus:outline-none focus:border-gloss-gold min-w-0"
                  data-testid={`scene-lines-${p.id}-${i}`} />
                <input value={sc.sub || ""} onChange={(e) => upd(i, { sub: e.target.value || null })} placeholder="gold sub"
                  className="w-20 bg-transparent border-b border-ink/20 font-mono text-[10px] px-1 py-0.5 focus:outline-none" data-testid={`scene-sub-${p.id}-${i}`} />
              </>
            )}
            <input type="number" step="0.1" min="0.6" max="4" value={sc.secs}
              onChange={(e) => upd(i, { secs: parseFloat(e.target.value) || 1 })}
              className="w-14 bg-transparent border-b border-ink/20 font-mono text-[10px] px-1 py-0.5 focus:outline-none shrink-0" data-testid={`scene-secs-${p.id}-${i}`} />
            <div className="flex gap-0.5 shrink-0">
              <button onClick={() => move(i, -1)} className="text-ink/40 hover:text-ink px-0.5" data-testid={`scene-up-${p.id}-${i}`}>↑</button>
              <button onClick={() => move(i, 1)} className="text-ink/40 hover:text-ink px-0.5" data-testid={`scene-down-${p.id}-${i}`}>↓</button>
              <button onClick={() => del(i)} className="text-red-400 hover:text-red-600 px-0.5" data-testid={`scene-del-${p.id}-${i}`}>✕</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={addCard} className="mt-2 overline text-[9px] px-2 py-1.5 border border-dashed border-ink/40 hover:border-ink" data-testid={`scene-add-${p.id}`}>+ Add a card</button>
      {p.voiceover && (
        <>
          <p className="overline text-[10px] text-ink/60 mt-3 mb-1">What the narrator says {vo !== (p.vo_script || "") || voice !== (p.voice || "fable") ? "— changed (new voice take ~3p)" : "— unchanged (free)"}</p>
          <textarea value={vo} onChange={(e) => setVo(e.target.value)} rows={3}
            className="w-full bg-white border border-ink/20 px-2 py-1.5 font-sans text-xs focus:outline-none focus:border-gloss-gold" data-testid={`storyboard-vo-${p.id}`} />
          <VoicePicker value={voice} onChange={setVoice} tid={`storyboard-voice-${p.id}`} />
        </>
      )}
      <div className="flex gap-2 mt-3">
        <button onClick={() => save(false)} disabled={!!busy} className="overline text-[10px] px-3 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors disabled:opacity-50" data-testid={`storyboard-save-${p.id}`}>
          {busy === "save" ? "Saving…" : "Save storyboard"}
        </button>
        <button onClick={() => save(true)} disabled={!!busy} className="overline text-[10px] px-4 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50" data-testid={`storyboard-render-${p.id}`}>
          {busy === "render" ? "Sending…" : "Cut the film"}
        </button>
      </div>
    </div>
  );
}

function ProductionCard({ p, onDelete, onPosted }) {
  const [postOpen, setPostOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [posting, setPosting] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [sbOpen, setSbOpen] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const src = p.video_path ? absUrl(p.video_path) : (p.local_url || null);
  const poster = p.poster_path ? absUrl(p.poster_path) : undefined;
  const publishing = p.ig_status === "publishing";

  const saveTitle = async () => {
    const t = titleDraft.trim();
    if (!t) return setEditingTitle(false);
    try {
      await client.patch(`/admin/productions/${p.id}`, { title: t });
      toast.success("Renamed.");
      setEditingTitle(false);
      onPosted();
    } catch {
      toast.error("Could not rename that one.");
    }
  };

  const aiWrite = async () => {
    setAiBusy(true);
    try {
      const r = await client.post(`/admin/productions/${p.id}/write-caption`, { instruction: aiNote, current: caption });
      setCaption(r.data.caption);
      toast.success("Caption written — tweak anything you like before posting.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The desk could not write that — try again.");
    } finally {
      setAiBusy(false);
    }
  };

  const post = async () => {
    if (!window.confirm("Post this video to Instagram now? It goes live immediately on the S/ME account.")) return;
    setPosting(true);
    try {
      await client.post(`/admin/productions/${p.id}/instagram-publish`, { caption: caption || p.caption || "" });
      toast.success("On its way to Instagram — live in a minute or two.");
      setPostOpen(false);
      onPosted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post that one.");
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="border border-ink/15 bg-white flex flex-col" data-testid={`production-${p.id}`}>
      {p.status === "ready" && src && (
        <video controls preload="none" poster={poster} src={src} className="w-full aspect-[9/16] max-h-80 object-contain bg-ink" data-testid={`production-video-${p.id}`} />
      )}
      {p.status === "planned" && (
        <div className="w-full bg-gloss-gold/15 border-b border-gloss-gold/40 px-4 py-2">
          <p className="overline text-[9px] text-ink" data-testid={`production-planned-${p.id}`}>Storyboard ready — review below, then cut</p>
        </div>
      )}
      {p.status === "rendering" && (
        <div className="w-full aspect-[9/16] max-h-80 bg-ink flex items-center justify-center">
          <p className="overline text-[10px] text-cream/70 animate-pulse" data-testid={`production-rendering-${p.id}`}>{p.mode === "stunt" ? (p.progress || "Staging the stunt — 5 to 10 minutes, it's worth it…") : "Cutting — a minute or three…"}</p>
        </div>
      )}
      {p.status === "failed" && (
        <div className="w-full aspect-[9/16] max-h-80 bg-ink flex flex-col items-center justify-center gap-3 p-4">
          <p className="font-mono text-[10px] text-red-400 text-center" data-testid={`production-error-${p.id}`}>{p.error || "That cut failed — try again."}</p>
          {p.mode !== "stunt" && p.plan?.scenes?.length > 0 && (
            <button onClick={() => setSbOpen(true)}
              className="overline text-[10px] px-3 py-2 bg-gloss-gold text-ink hover:bg-cream transition-colors" data-testid={`production-recut-${p.id}`}>
              Fix the storyboard & re-cut
            </button>
          )}
          {p.mode === "stunt" && (
            <button onClick={async () => {
              try { await client.post(`/admin/productions/${p.id}/restage`); toast.success("Staging it again — about five minutes."); onPosted?.(); }
              catch (e) { toast.error(e?.response?.data?.detail || "Could not restage."); }
            }} className="overline text-[10px] px-3 py-2 bg-gloss-gold text-ink hover:bg-cream transition-colors" data-testid={`production-restage-${p.id}`}>
              Stage it again
            </button>
          )}
        </div>
      )}
      <div className="p-4 flex-1 flex flex-col">
        {editingTitle ? (
          <div className="flex gap-2">
            <input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveTitle()} autoFocus
              className="flex-1 bg-cream border border-ink/20 px-2 py-1 font-serif text-base focus:outline-none focus:border-gloss-gold"
              data-testid={`production-title-input-${p.id}`} />
            <button onClick={saveTitle} className="overline text-[10px] px-2 py-1 bg-ink text-cream hover:bg-gloss-gold hover:text-ink" data-testid={`production-title-save-${p.id}`}>Save</button>
          </div>
        ) : (
          <p className="font-serif text-base text-ink leading-snug group flex items-start gap-1.5">
            {p.title}
            <button onClick={() => { setTitleDraft(p.title || ""); setEditingTitle(true); }}
              className="text-ink/30 hover:text-gloss-gold transition-colors mt-0.5" title="Rename"
              data-testid={`production-rename-${p.id}`}>
              <Pencil size={12} />
            </button>
          </p>
        )}
        <p className="font-mono text-[10px] text-ink/50 mt-1">
          {(FORMATS.find((f) => f.id === p.format)?.label) || p.format}
          {p.duration ? ` · ${p.duration}s` : ""}{p.size_mb ? ` · ${p.size_mb}MB` : ""}
        </p>
        {p.caption && <p className="font-sans text-xs text-ink/60 mt-2 line-clamp-3">{p.caption}</p>}
        <div className="mt-3 pt-3 border-t border-ink/10 flex items-center gap-2 flex-wrap">
          {p.status === "ready" && src && (
            <a href={src} download className="overline text-[10px] px-3 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors flex items-center gap-1.5" data-testid={`production-download-${p.id}`}>
              <Download size={11} />Download
            </a>
          )}
          {p.status === "ready" && src && (
            p.ig_status === "posted" && p.ig_permalink ? (
              <a href={p.ig_permalink} target="_blank" rel="noreferrer" className="overline text-[10px] px-3 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors" data-testid={`production-ig-live-${p.id}`}>On Instagram ✓</a>
            ) : (
              <button onClick={() => { setCaption(p.caption || ""); setPostOpen(!postOpen); }} disabled={publishing}
                className="overline text-[10px] px-3 py-2 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-60 flex items-center gap-1.5"
                data-testid={`production-ig-btn-${p.id}`}>
                <Send size={11} />{publishing ? "Posting…" : "Post to Instagram"}
              </button>
            )
          )}
          {p.plan?.scenes?.length > 0 && p.status !== "planned" && (
            <button onClick={() => setSbOpen(!sbOpen)} className="overline text-[10px] px-3 py-2 border border-ink/30 hover:bg-ink hover:text-cream transition-colors" data-testid={`production-storyboard-btn-${p.id}`}>
              Storyboard
            </button>
          )}
          <button onClick={() => onDelete(p)} className="overline text-[10px] px-3 py-2 border border-red-300 text-red-600 hover:bg-red-600 hover:text-white transition-colors flex items-center gap-1.5 ml-auto" data-testid={`production-delete-${p.id}`}>
            <Trash2 size={11} />Delete
          </button>
        </div>
        {postOpen && !publishing && (
          <div className="mt-3" data-testid={`production-ig-composer-${p.id}`}>
            <p className="overline text-[10px] text-ink/60 mb-1">Caption — the full text below is exactly what goes live</p>
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={8}
              className="w-full bg-cream border border-ink/20 px-3 py-2 font-sans text-xs focus:outline-none focus:border-gloss-gold"
              data-testid={`production-ig-caption-${p.id}`} />
            <div className="mt-2 flex gap-2">
              <input value={aiNote} onChange={(e) => setAiNote(e.target.value)}
                placeholder='Optional steer — e.g. "change Kayes to Hattie" or "punchier"'
                className="flex-1 bg-cream border border-ink/20 px-3 py-2 font-sans text-xs focus:outline-none focus:border-gloss-gold"
                data-testid={`production-ai-note-${p.id}`} />
              <button onClick={aiWrite} disabled={aiBusy}
                className="overline text-[10px] px-3 py-2 border border-gloss-gold text-ink hover:bg-gloss-gold transition-colors disabled:opacity-50 whitespace-nowrap"
                data-testid={`production-ai-write-${p.id}`}>
                {aiBusy ? "Writing…" : "Ask the desk to write it"}
              </button>
            </div>
            <p className="font-mono text-[9px] text-ink/40 mt-1">The desk's rewrite costs ~1p — editing by hand is free.</p>
            <button onClick={post} disabled={posting}
              className="mt-2 overline text-[10px] px-4 py-2.5 bg-ink text-cream hover:bg-gloss-gold hover:text-ink transition-colors disabled:opacity-50"
              data-testid={`production-ig-post-${p.id}`}>
              {posting ? "Sending…" : "Post now"}
            </button>
          </div>
        )}
        {p.ig_status === "failed" && p.ig_error && (
          <p className="font-mono text-[10px] text-red-600 mt-2" data-testid={`production-ig-error-${p.id}`}>{p.ig_error}</p>
        )}
      </div>
      {(p.status === "planned" || p.status === "drawing" || sbOpen) && p.plan?.scenes?.length > 0 && (
        p.mode === "create"
          ? <CreateBoard p={p} onChanged={() => { setSbOpen(false); onPosted(); }} />
          : <Storyboard p={p} onChanged={() => { setSbOpen(false); onPosted(); }} />
      )}
    </div>
  );
}

function StuntStudio({ onStarted }) {
  const [tab, setTab] = useState("presets");
  const [opts, setOpts] = useState(null);
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [stunt, setStunt] = useState("surprise");
  const [location, setLocation] = useState("");
  const [customLoc, setCustomLoc] = useState("");
  const [busy, setBusy] = useState(false);
  const [direction, setDirection] = useState("");
  const [cast, setCast] = useState([]);
  const [dirLoc, setDirLoc] = useState("");
  const [dirCustomLoc, setDirCustomLoc] = useState("");
  const [dirLength, setDirLength] = useState("full");
  useEffect(() => {
    client.get("/admin/productions/stunt-options").then((r) => { setOpts(r.data); setLocation(r.data.locations?.[0] || ""); }).catch(() => {});
    client.get("/admin/members").then((r) => setMembers(r.data || [])).catch(() => {});
  }, []);
  const go = async () => {
    if (!memberId) { toast.error("Pick a member first."); return; }
    setBusy(true);
    try {
      await client.post("/admin/productions/stunt", { member_id: memberId, stunt, location: location === "__custom" ? customLoc : location });
      toast.success("The stunt is being staged — about five minutes. It lands in the library below.");
      onStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the stunt.");
    }
    setBusy(false);
  };
  const goDirector = async () => {
    if (direction.trim().length < 12) { toast.error("Write the direction first — a sentence or two about the stunt you want."); return; }
    setBusy(true);
    try {
      await client.post("/admin/productions/stunt-director", {
        prompt: direction, member_ids: cast,
        location: dirLoc === "__custom" ? dirCustomLoc : dirLoc,
        length: dirLength,
      });
      toast.success("The director's cut is being staged — a full production takes ten to twenty minutes. It lands in the library below.");
      setDirection("");
      onStarted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the stunt.");
    }
    setBusy(false);
  };
  const chosen = opts?.stunts?.find((s) => s.key === stunt);
  const tabBtn = (on) => `overline text-[10px] px-3 py-1.5 border transition-colors ${on ? "bg-gloss-gold text-ink border-gloss-gold" : "border-cream/30 text-cream/70 hover:border-cream"}`;
  return (
    <div className="border border-gloss-gold/60 bg-ink text-cream p-5 sm:p-6 mb-8" data-testid="stunt-studio">
      <div className="flex items-center gap-3 flex-wrap">
        <p className="overline text-gloss-gold">Stunt Studio</p>
        <div className="flex gap-2">
          <button onClick={() => setTab("presets")} className={tabBtn(tab === "presets")} data-testid="stunt-tab-presets">Set pieces</button>
          <button onClick={() => setTab("director")} className={tabBtn(tab === "director")} data-testid="stunt-tab-director">Director mode</button>
        </div>
      </div>
      {tab === "presets" && (<>
        <p className="font-sans text-sm text-cream/60 mt-2 mb-4">Impossible street moments starring your members — pick a star, a stunt and a street. Under £1 a film.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="stunt-member-select">
            <option value="" className="text-ink">The star — pick a member…</option>
            {members.map((m) => <option key={m.id} value={m.id} className="text-ink">{m.business_name}</option>)}
          </select>
          <select value={stunt} onChange={(e) => setStunt(e.target.value)}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="stunt-template-select">
            {(opts?.stunts || []).map((s) => <option key={s.key} value={s.key} className="text-ink">{s.label}</option>)}
          </select>
          <select value={location} onChange={(e) => setLocation(e.target.value)}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="stunt-location-select">
            {(opts?.locations || []).map((l) => <option key={l} value={l} className="text-ink">{l.replace(/^a |^the /, "")}</option>)}
            <option value="__custom" className="text-ink">Somewhere else…</option>
          </select>
          <button onClick={go} disabled={busy} className="overline text-[11px] px-4 py-2.5 bg-gloss-gold text-ink hover:bg-cream transition-colors disabled:opacity-50" data-testid="stunt-go">
            {busy ? "Staging…" : "Stage the stunt"}
          </button>
        </div>
        {location === "__custom" && (
          <input value={customLoc} onChange={(e) => setCustomLoc(e.target.value)} placeholder="e.g. a cobbled lane in York city centre"
            className="mt-3 w-full bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream placeholder:text-cream/40" data-testid="stunt-location-custom" />
        )}
        {chosen && <p className="font-mono text-[10px] text-cream/45 mt-3" data-testid="stunt-desc">{chosen.desc} — their real cover art, full-HD 1080, ends on the join card.</p>}
      </>)}
      {tab === "director" && (<>
        <p className="font-sans text-sm text-cream/60 mt-2 mb-4">You direct, the studio stages it to the premiere standard — one setting, multiple scenes, real faces and covers, flash cuts and the join card. Members optional: leave the cast empty for a pure S/ME promo film.</p>
        <textarea value={direction} onChange={(e) => setDirection(e.target.value)} rows={3}
          placeholder="e.g. A giant glossy S/ME magazine sails down the Thames on a barge past Tower Bridge while crowds on the banks film it"
          className="w-full bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream placeholder:text-cream/40" data-testid="director-prompt" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <select value="" onChange={(e) => { if (e.target.value && cast.length < 3) setCast([...cast, e.target.value]); }}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="director-add-member">
            <option value="" className="text-ink">{cast.length ? "Add another star (optional)…" : "The cast — add a member (optional)…"}</option>
            {members.filter((m) => !cast.includes(m.id)).map((m) => <option key={m.id} value={m.id} className="text-ink">{m.business_name}</option>)}
          </select>
          <select value={dirLength} onChange={(e) => setDirLength(e.target.value)}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="director-length-select">
            <option value="full" className="text-ink">The full production — 4-5 scenes, ~30s</option>
            <option value="punchy" className="text-ink">Punchy — 2 scenes, ~15s</option>
          </select>
          <select value={dirLoc} onChange={(e) => setDirLoc(e.target.value)}
            className="bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream" data-testid="director-location-select">
            <option value="" className="text-ink">Let the direction decide the street</option>
            {(opts?.locations || []).map((l) => <option key={l} value={l} className="text-ink">{l.replace(/^a |^the /, "")}</option>)}
            <option value="__custom" className="text-ink">Somewhere else…</option>
          </select>
          <button onClick={goDirector} disabled={busy} className="overline text-[11px] px-4 py-2.5 bg-gloss-gold text-ink hover:bg-cream transition-colors disabled:opacity-50" data-testid="director-go">
            {busy ? "Staging…" : "Stage the director's cut"}
          </button>
        </div>
        {dirLoc === "__custom" && (
          <input value={dirCustomLoc} onChange={(e) => setDirCustomLoc(e.target.value)} placeholder="e.g. the seafront at Whitby"
            className="mt-3 w-full bg-transparent border border-cream/30 px-3 py-2.5 font-sans text-sm text-cream placeholder:text-cream/40" data-testid="director-location-custom" />
        )}
        {cast.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3" data-testid="director-cast-chips">
            {cast.map((id) => {
              const m = members.find((x) => x.id === id);
              return (
                <span key={id} className="inline-flex items-center gap-2 border border-gloss-gold/60 px-2.5 py-1 font-sans text-xs text-cream" data-testid={`director-cast-chip-${id}`}>
                  {m?.business_name || "Member"}
                  <button onClick={() => setCast(cast.filter((c) => c !== id))} className="text-cream/60 hover:text-cream" data-testid={`director-cast-remove-${id}`}>×</button>
                </span>
              );
            })}
          </div>
        )}
        <p className="font-mono text-[10px] text-cream/45 mt-3" data-testid="director-desc">One setting, {dirLength === "punchy" ? "two scenes" : "four to five scenes"} filmed like premium coverage — your direction word for word, flashbulb cuts, music bed, ends on the join card.</p>
      </>)}
    </div>
  );
}

export default function VideoDesk() {
  const [items, setItems] = useState(null);
  const [shots, setShots] = useState(null);
  const load = () => client.get("/admin/productions").then((r) => setItems(r.data)).catch(() => {});
  const loadShots = () => client.get("/admin/productions/shots").then((r) => setShots(r.data)).catch(() => {});
  useEffect(() => { load(); loadShots(); }, []);
  useEffect(() => {
    if (shots?.status !== "refreshing") return;
    const t = setInterval(loadShots, 7000);
    return () => clearInterval(t);
  }, [shots]);

  const refreshShots = async () => {
    try {
      await client.post("/admin/productions/refresh-shots");
      toast.success("Re-photographing the magazine — about a minute.");
      loadShots();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start that.");
    }
  };

  const recordTour = async () => {
    if (!window.confirm("Re-record the live magazine tour now? The desk walks the live site in a fresh browser and re-cuts the narrated demo — about five minutes. The narration is reused, so it costs nothing.")) return;
    try {
      await client.post("/admin/productions/record-tour");
      toast.success("Recording the live tour — about five minutes. The fresh cut lands in the library.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start that.");
    }
  };

  useEffect(() => {
    if (!items?.some((p) => ["rendering", "drawing"].includes(p.status) || p.ig_status === "publishing")) return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [items]);

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from the library? This cannot be undone.`)) return;
    try {
      await client.delete(`/admin/productions/${p.id}`);
      toast.success("Gone.");
      load();
    } catch {
      toast.error("Could not delete that one.");
    }
  };

  return (
    <div data-testid="video-desk">
      <Header title="Video Desk" sub="Brief the desk, get a finished video — reels, explainers and features, narrated and scored. Everything lives in the library below." />
      <div className="flex items-center gap-3 mb-6 -mt-2 flex-wrap" data-testid="video-desk-shots-bar">
        <button onClick={refreshShots} disabled={shots?.status === "refreshing"}
          className="overline text-[10px] px-3 py-2 border border-ink/25 hover:border-ink disabled:opacity-50 flex items-center gap-1.5"
          data-testid="video-desk-refresh-shots">
          <RefreshCw size={11} className={shots?.status === "refreshing" ? "animate-spin" : ""} />
          {shots?.status === "refreshing" ? "Re-photographing…" : "Refresh magazine shots"}
        </button>
        <button onClick={recordTour} disabled={(items || []).some((p) => p.seed === "live-magazine-tour" && p.status === "rendering")}
          className="overline text-[10px] px-3 py-2 border border-gloss-gold hover:bg-gloss-gold disabled:opacity-50 flex items-center gap-1.5"
          data-testid="video-desk-record-tour">
          <RefreshCw size={11} className={(items || []).some((p) => p.seed === "live-magazine-tour" && p.status === "rendering") ? "animate-spin" : ""} />
          {(items || []).some((p) => p.seed === "live-magazine-tour" && p.status === "rendering") ? "Recording the tour…" : "Re-record the live tour"}
        </button>
        <p className="font-mono text-[10px] text-ink/50" data-testid="video-desk-shots-status">
          {shots?.status === "failed" ? `Last attempt failed: ${shots.error || "try again"}` :
            shots?.at ? `Magazine last photographed ${new Date(shots.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} — new videos use these shots` : ""}
        </p>
      </div>
      <Composer onCut={load} />
      <StuntStudio onStarted={load} />
      <div className="flex items-center justify-between mb-4">
        <p className="overline text-ink">The library {items ? `— ${items.length}` : ""}</p>
        <button onClick={load} className="overline text-[10px] px-3 py-2 border border-ink/25 hover:border-ink flex items-center gap-1.5" data-testid="video-desk-refresh">
          <RefreshCw size={11} />Refresh
        </button>
      </div>
      {!items && <p className="font-sans text-sm text-ink/50">Loading the library…</p>}
      {items && items.length === 0 && <p className="font-sans text-sm text-ink/50">Nothing here yet — cut your first video above.</p>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="video-desk-library">
        {(items || []).map((p) => <ProductionCard key={p.id} p={p} onDelete={del} onPosted={load} />)}
      </div>
    </div>
  );
}
